import { useTranslation } from 'react-i18next'
import { DownloadIcon } from '@heroicons/react/outline'
import { useTw } from '../../../../../../../../utils/Styles'
import { ConversationQuery } from '../../../Conversation.generated'
import { formatBytes, shortenString } from '../../../../../../../../utils'
import { AttachmentIcon } from '../../../../../../../../utils/AttachmentIcon'

const imgexts = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp']
type Attachment =
  ConversationQuery['Conversation']['messages'][0]['attachments'][0]

type Props = {
  received: boolean
  attachments: Array<Attachment>
}

function BigAttachment({ attachment }: { attachment: Attachment }) {
  const tw = useTw()
  const [t] = useTranslation('common')
  return (
    <a
      download
      target="_blank"
      href={attachment.url}
      rel="noreferrer noopener"
      className={tw`relative block group rounded overflow-hidden cursor-pointer`}
    >
      <div
        className={tw`invisible group-hover:visible absolute top-0 left-0 right-0 bottom-0 bg-neutral-800 opacity-60`}
      />
      <div
        className={tw`invisible group-hover:visible absolute top-0 left-0 right-0 bottom-0`}
      >
        <div className={tw`flex flex-col justify-center items-center h-full`}>
          <DownloadIcon
            className={tw`bg-white rounded-full p-2 h-10 w-10 text-neutral-400`}
          />
          <span className={tw`text-white pt-1.5`}>
            {t('conversation.upload.download_image', {
              size: formatBytes(attachment.size),
            })}
          </span>
        </div>
      </div>
      <img
        src={attachment.url}
        alt={attachment.fileName}
        className={tw`h-full w-full`}
      />
    </a>
  )
}

function Attachment({ attachment }: { attachment: Attachment }) {
  const tw = useTw()
  const [t] = useTranslation('common')

  return (
    <a
      download
      target="_blank"
      href={attachment.url}
      rel="noreferrer noopener"
      className={tw`flex cursor-pointer p-3 border border-neutral-300 hover:border-neutral-400 rounded`}
    >
      <AttachmentIcon
        ext={attachment.extension}
        filename={attachment.fileName}
        url={imgexts.includes(attachment.extension) ? attachment.url : null}
      />
      <div>
        <p className={tw`whitespace-nowrap text-neutral-400 font-bold`}>
          {shortenString(attachment.fileName)}
        </p>
        <p className={tw`whitespace-nowrap uppercase text-sm text-neutral-350`}>
          {t('conversation.upload.download_file', {
            size: formatBytes(attachment.size),
            ext: attachment.extension,
          })}
        </p>
      </div>
      <DownloadIcon className={tw`self-center ml-4 text-neutral-400 h-6 w-6`} />
    </a>
  )
}

export function Attachments({ attachments, received }: Props) {
  const tw = useTw()
  if (!attachments.length) return null

  if (attachments.length === 1 && imgexts.includes(attachments[0].extension))
    return <BigAttachment attachment={attachments[0]} />

  return (
    <div
      className={tw`flex flex-wrap max-w-full gap-3 ${
        received ? 'justify-end' : 'justify-start'
      }`}
    >
      {attachments.map((attachment) => (
        <Attachment key={attachment._id} attachment={attachment} />
      ))}
    </div>
  )
}
